import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  FileText,
  DollarSign,
  TrendingUp,
  Award,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  UserCog,
  UserCheck,
  UserPlus,
  GraduationCap,
  Building2,
} from 'lucide-react';
import { collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import { firestore } from '../../database/firebase';

const AnalyticsOverview = () => {
  const [metrics, setMetrics] = useState({
    totalApplications: 0,
    activeUsers: 0,
    grantAllocations: 0,
    cohortProgress: 0,
    pendingApprovals: 0,
    completedPrograms: 0,
    averageHealthScore: 0,
    systemAlerts: 0,
    clients: 0,
    admins: 0,
    moderators: 0,
    coaches: 0,
    alumni: 0,
    bifApplications: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(
    () => window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  useEffect(() => {
    const unsubApplications = onSnapshot(collection(firestore, 'applications'), snapshot => {
      setMetrics(prev => ({ ...prev, totalApplications: snapshot.size }));
    });
    const unsubActiveUsers = onSnapshot(
      query(collection(firestore, 'users'), where('status', '==', 'active')),
      snapshot => {
        setMetrics(prev => ({ ...prev, activeUsers: snapshot.size }));
      }
    );
    const unsubGrantAllocations = onSnapshot(
      query(collection(firestore, 'grants'), where('status', '==', 'approved')),
      snapshot => {
        let total = 0;
        snapshot.forEach(doc => {
          const data = doc.data();
          total += Number(data.amount || 0);
        });
        setMetrics(prev => ({ ...prev, grantAllocations: total }));
      }
    );
    const unsubPendingApprovals = onSnapshot(
      query(collection(firestore, 'applications'), where('status', '==', 'pending')),
      snapshot => {
        setMetrics(prev => ({ ...prev, pendingApprovals: snapshot.size }));
      }
    );
    const unsubCompletedPrograms = onSnapshot(
      query(collection(firestore, 'programs'), where('status', '==', 'completed')),
      snapshot => {
        setMetrics(prev => ({ ...prev, completedPrograms: snapshot.size }));
      }
    );
    const unsubSystemAlerts = onSnapshot(collection(firestore, 'system_alerts'), snapshot => {
      setMetrics(prev => ({ ...prev, systemAlerts: snapshot.size }));
    });
    const unsubClients = onSnapshot(collection(firestore, 'clients'), snapshot => {
      setMetrics(prev => ({ ...prev, clients: snapshot.size }));
    });
    const unsubAdmins = onSnapshot(collection(firestore, 'admin'), snapshot => {
      setMetrics(prev => ({ ...prev, admins: snapshot.size }));
    });
    const unsubModerators = onSnapshot(collection(firestore, 'moderator'), snapshot => {
      setMetrics(prev => ({ ...prev, moderators: snapshot.size }));
    });
    const unsubCoaches = onSnapshot(collection(firestore, 'coaches'), snapshot => {
      setMetrics(prev => ({ ...prev, coaches: snapshot.size }));
    });
    const unsubAlumni = onSnapshot(collection(firestore, 'alumni'), snapshot => {
      setMetrics(prev => ({ ...prev, alumni: snapshot.size }));
    });
    const unsubBifApplications = onSnapshot(collection(firestore, 'bif_applications'), snapshot => {
      setMetrics(prev => ({ ...prev, bifApplications: snapshot.size }));
    });
    const fetchCohortStats = async () => {
      const cohortSnap = await getDocs(collection(firestore, 'cohorts'));
      let totalProgress = 0;
      let totalHealth = 0;
      let count = 0;
      cohortSnap.forEach(doc => {
        const data = doc.data();
        totalProgress += Number(data.completionRate || 0);
        totalHealth += Number(data.averageHealthScore || 0);
        count++;
      });
      setMetrics(prev => ({
        ...prev,
        cohortProgress: count ? Math.round(totalProgress / count) : 0,
        averageHealthScore: count ? Math.round(totalHealth / count) : 0,
      }));
      setIsLoading(false);
    };
    fetchCohortStats();
    const darkListener = e => setDarkMode(e.matches);
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', darkListener);
    return () => {
      unsubApplications();
      unsubActiveUsers();
      unsubGrantAllocations();
      unsubPendingApprovals();
      unsubCompletedPrograms();
      unsubSystemAlerts();
      unsubClients();
      unsubAdmins();
      unsubModerators();
      unsubCoaches();
      unsubAlumni();
      unsubBifApplications();
      window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', darkListener);
    };
  }, []);

  const kpiCards = [
    {
      title: 'Clients',
      value: metrics.clients.toLocaleString(),
      icon: Building2,
      color: 'accent',
      description: 'Total clients',
    },
    {
      title: 'Admins',
      value: metrics.admins.toLocaleString(),
      icon: UserCog,
      color: 'primary',
      description: 'Total admins',
    },
    {
      title: 'Business Advisors',
      value: metrics.moderators.toLocaleString(),
      icon: UserCheck,
      color: 'success',
      description: 'Total Business Advisors',
    },
    {
      title: 'Coaches and Mentors',
      value: metrics.coaches.toLocaleString(),
      icon: GraduationCap,
      color: 'success',
      description: 'Total coaches',
    },
    {
      title: 'Alumni',
      value: metrics.alumni.toLocaleString(),
      icon: UserPlus,
      color: 'accent',
      description: 'Total alumni',
    },
    // {
    //   title: 'BIF Applications',
    //   value: metrics.bifApplications.toLocaleString(),
    //   icon: FileText,
    //   color: 'accent',
    //   description: 'BIF applications',
    // },
    {
      title: 'Total Applications',
      value: metrics.totalApplications.toLocaleString(),
      icon: FileText,
      color: 'accent',
      description: 'Applications this month',
    },
    // {
    //   title: 'Grant Allocations',
    //   value: `P ${metrics.grantAllocations.toLocaleString()}`,
    //   icon: DollarSign,
    //   color: 'accent',
    //   description: 'Total funds allocated',
    // },
    {
      title: 'Cohort Progress',
      value: `${metrics.cohortProgress}%`,
      icon: TrendingUp,
      color: 'primary',
      description: 'Average completion rate',
    },
    {
      title: 'Pending Approvals',
      value: metrics.pendingApprovals,
      icon: Clock,
      color: 'warning',
      description: 'Requires attention',
    },
    {
      title: 'Completed Programs',
      value: metrics.completedPrograms,
      icon: CheckCircle,
      color: 'success',
      description: 'Successfully completed',
    },
    {
      title: 'Health Score',
      value: `${metrics.averageHealthScore}%`,
      icon: Award,
      color: 'success',
      description: 'Average business health',
    },
    {
      title: 'System Alerts',
      value: metrics.systemAlerts,
      icon: AlertCircle,
      color: 'error',
      description: 'Requires immediate action',
    },
  ];

  const colorClasses = {
    accent: 'bg-accent',
    success: 'bg-success',
    primary: 'bg-primary',
    warning: 'bg-warning',
    error: 'bg-error',
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(8)].map((_, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: index * 0.1 }}
            className="rounded-xl border border-accent/20 bg-bg-card p-6 shadow-sm dark:border-accent/40 dark:bg-nav"
          >
            <div className="animate-pulse">
              <div className="mb-4 flex items-center justify-between">
                <div className="h-10 w-10 rounded-lg bg-accent/10 dark:bg-accent/90"></div>
                <div className="h-4 w-16 rounded bg-accent/10 dark:bg-accent/90"></div>
              </div>
              <div className="space-y-2">
                <div className="h-6 w-24 rounded bg-accent/10 dark:bg-accent/90"></div>
                <div className="h-8 w-32 rounded bg-accent/10 dark:bg-accent/90"></div>
                <div className="h-3 w-20 rounded bg-accent/10 dark:bg-accent/90"></div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-4"
    >
      {kpiCards.map((card, index) => {
        const Icon = card.icon;
        return (
          <motion.div
            key={card.title}
            variants={cardVariants}
            className="flex flex-col gap-2 rounded-xl bg-bg-card p-4 shadow-sm transition-colors duration-300 dark:bg-nav md:p-6"
          >
            <div className="flex items-center gap-4">
              <div className={`rounded-lg p-3 ${colorClasses[card.color]}`}>
                <Icon className="h-6 w-6 text-bg-card" />
              </div>
              <div>
                <h3 className="text-xs font-medium text-gray-900 dark:text-gray-100 md:text-sm">
                  {card.title}
                </h3>
                <p className="text-lg font-semibold text-black dark:text-gray-100 md:text-2xl">
                  {card.value}
                </p>
              </div>
            </div>
            <div className="text-[10px] text-gray-900/60 md:text-xs">{card.description}</div>
          </motion.div>
        );
      })}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setDarkMode(dm => !dm)}
          className="rounded-full bg-primary p-3 text-bg-card shadow-lg transition-colors hover:bg-primary/90 dark:bg-primary dark:hover:bg-primary/80"
          aria-label="Toggle dark mode"
        >
          {darkMode ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 3v1m0 16v1m8.66-13.66l-.71.71M4.05 19.95l-.71.71M21 12h-1M4 12H3m16.66 5.66l-.71-.71M4.05 4.05l-.71-.71M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 12.79A9 9 0 1111.21 3a7 7 0 109.79 9.79z"
              />
            </svg>
          )}
        </button>
      </div>
    </motion.div>
  );
};

export default AnalyticsOverview;
